import React, { useEffect, useState } from "react";
import api from "../api/client";
import { ApiToken, ApiTokenCreateResponse } from "../types";
import { formatDate, formatRelative } from "../utils/datetime";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import ActionButton from "./ActionButton";

/**
 * API tokens — long-lived bearer credentials for scripts, CI and integrations.
 *
 * Moved off the Security & MFA page. Security & MFA is about how a HUMAN proves
 * who they are — authenticator app, backup codes, session, password. An API
 * token is the opposite: a credential for something that is not a person and
 * never sees a login screen. Sitting them together made the security page a
 * grab-bag and buried the tokens where nobody looked for them.
 *
 * They live under Integrations now, next to the other machine-to-machine
 * plumbing (outbound webhooks, SSO providers). The token is still scoped to the
 * creating USER and inherits that user's permissions — that is a property of
 * the token, not a reason to file it under personal security.
 */
export default function ApiTokensCard() {
  // ── State ────────────────────────────────────────────────────────
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [creatingToken, setCreatingToken] = useState(false);
  const [newTokenName, setNewTokenName] = useState("");
  const [newTokenExpiresDays, setNewTokenExpiresDays] = useState<string>("");
  const [createdToken, setCreatedToken] = useState<ApiTokenCreateResponse | null>(null);
  const [tokenBusy, setTokenBusy] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [copyConfirm, setCopyConfirm] = useState(false);
  const tokenSort = useSort("created_at", "desc");

  const loadTokens = () => {
    api
      .get("/api/v1/auth/api-tokens")
      .then((r) => setTokens(r.data))
      .catch(() => setTokens([]));
  };
  useEffect(() => {
    loadTokens();
  }, []);

  const createToken = async () => {
    setTokenError("");
    if (!newTokenName.trim()) {
      setTokenError("Name is required");
      return;
    }
    setTokenBusy(true);
    try {
      const body: Record<string, unknown> = { name: newTokenName.trim() };
      const days = parseInt(newTokenExpiresDays, 10);
      if (Number.isFinite(days) && days > 0) {
        const exp = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
        body.expires_at = exp.toISOString();
      }
      const { data } = await api.post("/api/v1/auth/api-tokens", body);
      setCreatedToken(data);
      setCreatingToken(false);
      setNewTokenName("");
      setNewTokenExpiresDays("");
      loadTokens();
    } catch (err: any) {
      setTokenError(err.response?.data?.detail || "Couldn't create token.");
    } finally {
      setTokenBusy(false);
    }
  };

  const revokeToken = async (t: ApiToken) => {
    if (!window.confirm(`Revoke token "${t.name}"? Anything using it will stop working.`)) {
      return;
    }
    try {
      await api.delete(`/api/v1/auth/api-tokens/${t.id}`);
      loadTokens();
    } catch (err: any) {
      alert(err.response?.data?.detail || "Revoke failed");
    }
  };

  const copyCreatedToken = async () => {
    if (!createdToken?.token) return;
    try {
      await navigator.clipboard.writeText(createdToken.token);
      setCopyConfirm(true);
      setTimeout(() => setCopyConfirm(false), 2000);
    } catch {
      alert("Couldn't copy automatically — select the token and copy manually.");
    }
  };

  return (
    <>
      {/* ── API tokens ──────────────────────────────────────────────────── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-5 mt-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">API tokens</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Long-lived bearer tokens for integrations, scripts, and CI. Use as
              <code className="ml-1 bg-gray-100 dark:bg-gray-700 dark:text-gray-200 px-1 rounded">Authorization: Bearer ag_…</code>.
              Tokens have the same permissions as your user account.
            </p>
          </div>
          <button
            onClick={() => {
              setCreatingToken(true);
              setNewTokenName("");
              setNewTokenExpiresDays("");
              setTokenError("");
            }}
            className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg"
          >
            + New token
          </button>
        </div>

        {tokens.length === 0 ? (
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-3">No active tokens.</p>
        ) : (
          <table className="w-full text-sm mt-4">
            <thead className="text-xs uppercase text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
              <tr>
                <SortableTh label="Name" sortKey="name" sort={tokenSort.sort} onSort={tokenSort.toggle} />
                <SortableTh label="Prefix" sortKey="prefix" sort={tokenSort.sort} onSort={tokenSort.toggle} />
                <SortableTh label="Last used" sortKey="last_used_at" sort={tokenSort.sort} onSort={tokenSort.toggle} />
                <SortableTh label="Expires" sortKey="expires_at" sort={tokenSort.sort} onSort={tokenSort.toggle} />
                <SortableTh label="Created" sortKey="created_at" sort={tokenSort.sort} onSort={tokenSort.toggle} />
                <th />
              </tr>
            </thead>
            <tbody>
              {sortRows(tokens, tokenSort.sort, (t, key) => {
                switch (key) {
                  case "name":
                    return (t.name || "").toLowerCase();
                  case "prefix":
                    return (t.prefix || "").toLowerCase();
                  case "last_used_at":
                    return t.last_used_at ? Date.parse(t.last_used_at) : 0;
                  case "expires_at":
                    return t.expires_at ? Date.parse(t.expires_at) : 0;
                  default:
                    return t.created_at ? Date.parse(t.created_at) : 0;
                }
              }).map((t) => (
                <tr key={t.id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <td className="py-2 pr-3 text-gray-900 dark:text-gray-100">{t.name}</td>
                  <td className="py-2 pr-3 font-mono text-xs text-gray-500 dark:text-gray-400">{t.prefix}…</td>
                  <td className="py-2 pr-3 text-xs text-gray-500 dark:text-gray-400">
                    {t.last_used_at ? (
                      formatRelative(t.last_used_at)
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">never</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-gray-500 dark:text-gray-400">
                    {t.expires_at ? formatDate(t.expires_at) : "—"}
                  </td>
                  <td className="py-2 pr-3 text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(t.created_at)}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    <button
                      onClick={() => revokeToken(t)}
                      className="text-red-600 dark:text-red-400 text-xs hover:underline"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create-token modal */}
      {creatingToken && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New API token</h2>
            <div>
              <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Name</label>
              <input
                value={newTokenName}
                onChange={(e) => setNewTokenName(e.target.value)}
                autoFocus
                placeholder="CI pipeline, Backup script, ..."
                className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
              />
            </div>
            <div>
              <label className="text-xs uppercase text-gray-500 dark:text-gray-400">Expires in (days)</label>
              <input
                value={newTokenExpiresDays}
                onChange={(e) => setNewTokenExpiresDays(e.target.value)}
                type="number"
                min={1}
                placeholder="Leave blank for no expiry"
                className="w-full border dark:bg-gray-900 dark:border-gray-600 dark:text-gray-100 rounded-lg px-3 py-2 text-sm mt-1"
              />
            </div>
            {tokenError && (
              <p className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/40 px-3 py-2 rounded">{tokenError}</p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setCreatingToken(false)}
                className="border dark:border-gray-600 text-sm px-4 py-1.5 rounded-lg text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={createToken}
                disabled={tokenBusy}
                className="bg-blue-600 text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-50"
              >
                {tokenBusy ? "Creating…" : "Create token"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Token-created modal: shows plaintext ONCE */}
      {createdToken && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto space-y-4">
            <h2 className="text-lg font-semibold text-green-700 dark:text-green-400">Token created — copy it now</h2>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              This is the only time you'll see the token. Copy it now and store it somewhere safe —
              we only keep a hash on our side.
            </p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={createdToken.token}
                className="flex-1 font-mono text-sm bg-gray-50 dark:bg-gray-900 dark:text-gray-100 border dark:border-gray-600 rounded px-3 py-2"
              />
              <ActionButton onAction={() => copyCreatedToken()} variant="raw" className="bg-blue-600 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap" pendingLabel="Creating…">
                {copyConfirm ? "Copied ✓" : "Copy"}
              </ActionButton>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Send it as{" "}
              <code className="bg-gray-100 dark:bg-gray-700 dark:text-gray-200 px-1 rounded">
                Authorization: Bearer {createdToken.token}
              </code>{" "}
              from your script or integration. Revoke this token any time from the list above.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setCreatedToken(null)}
                className="bg-gray-800 dark:bg-gray-600 text-white text-sm px-4 py-1.5 rounded-lg"
              >
                I've saved it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
